import { IAxisLabelStyle, IAxisStyleSettings, IStyleSettings } from 'store/workspace/types';

export const MAPPINGS = {
  shape: [{ id: "Circle", url: `${window.location.origin}/circle.png`, plotValue: 'circle' }, { id: "Square", url: `${window.location.origin}/square.png`, plotValue: 'square' }, { id: "Triangle", url: `${window.location.origin}/triangle.png`, plotValue: 'triangle-up' }],
  // value is the API's ProductionMarkerType: Marker = 1 draws the markers alone, LineMarker = 2
  // draws the line. Reversing them inverts the series on every save, because every save rewrites
  // this field alongside whichever one the user actually changed.
  shapeProduction: [{ name: "Point", value: 1 }, { name: "Line", value: 2 }],
  sizes: ['x1', 'x2', 'x3', 'x4', 'x5'],
  markerSizes: [1, 3, 7, 14, 21],
  fontSizes: {12: {name: 'Small', value: '12px'}, 14: {name: 'Small', value: '14px'}, 16: {name: 'Medium', value: '16px'}, 18: {name: 'Medium', value: '18px'}, 20: {name: 'Large', value: '20px'}},
  // plotValue must be a valid Plotly `textposition` ('vertical horizontal'); single-word
  // values silently coerce to Plotly's 'middle center' default (labels always centred).
  positions: [{label: 'Center', value: 'center', plotValue: 'middle center'}, {label: 'Above', value: 'top', plotValue: 'top center'}, {label: 'Below', value: 'bottom', plotValue: 'bottom center'}, {label: 'Right', value: 'start', plotValue: 'middle right'}, {label: 'Left', value: 'end', plotValue: 'middle left'}]
}

// Function to get consistent marker size for both map and scatter plot
export const getMarkerSize = (markerSizeType: number): number => {
  if (!markerSizeType || markerSizeType < 1 || markerSizeType > 5) {
    return MAPPINGS.markerSizes[2]; // Default to x3 (index 2)
  }
  return MAPPINGS.markerSizes[markerSizeType - 1];
};

const DEFAULT_AXIS_FONT = "Arial"

const axisFontSize = (fontSizeType: number | undefined, fallback: number) =>
  MAPPINGS.fontSizes[fontSizeType as keyof typeof MAPPINGS.fontSizes] ||
  MAPPINGS.fontSizes[fallback as keyof typeof MAPPINGS.fontSizes]

const axisFont = (font: string | undefined) => ({
  name: font || DEFAULT_AXIS_FONT,
  family: font || DEFAULT_AXIS_FONT
})

export const processAxisTitleOptions = (options: any): Partial<IAxisStyleSettings> => {
  const axis = (option: any) => ({
    font: axisFont(option?.font),
    sizeFont: axisFontSize(option?.fontSizeType, 14),
    sizeValueFont: axisFontSize(option?.valueFontSizeType, 12),
  })

  return {
    title: {
      font: axisFont(options?.Title?.font),
      sizeFont: axisFontSize(options?.Title?.fontSizeType, 18),
    },
    x: axis(options?.XAxis),
    y: axis(options?.YAxis),
    y2: axis(options?.Y2Axis),
  }
}

// Left to itself plotly tints each hover box with the colour of whatever it is hovering, so a
// chart's boxes come out in as many colours as it has series. Every plot wears this instead.
//
// A factory rather than a shared constant: plotly writes back into the hover-label object it is
// handed - handleHoverLabelDefaults assigns to opts.bgcolor, opts.bordercolor and opts.font - so a
// single instance passed to several charts carries whatever the last one to render left on it.
export const plotHoverLabel = () => ({
  bgcolor: "#FFF",
  bordercolor: "#939495",
  align: "left" as const,
  font: {
    family: "cursive",
    size: 14
  }
})

// Plotly's tickfont wants a number, while the store holds the CSS string the popup renders its
// dropdown with. 12 is Plotly's own default, so an axis with nothing saved keeps its old size.
export const axisTickFont = (axis?: IAxisLabelStyle) => ({
  size: parseInt(axis?.sizeValueFont?.value || '') || 12,
})

export const processSettings = (data: any): any => {
  return {
    color: data?.markerOption?.color,
    font: {name: data?.labelOption?.font, family: data?.labelOption?.font},
    shape: MAPPINGS.shape[data?.markerOption?.shapeType - 1],
    sizeFont: MAPPINGS.fontSizes[data?.labelOption?.fontSizeType as keyof typeof MAPPINGS.fontSizes],
    sizeShape: MAPPINGS.sizes[data?.markerOption?.markerSizeType - 1] as IStyleSettings['sizeShape'],
    position: MAPPINGS.positions[data?.labelOption?.positionType - 1],
    border: data?.markerOption?.hasBorder,
    optionId: data?.id
  }
}

export const processSettingsToBody = (data: any): any => {
  return {
    markerOption: {
      color: data?.color,
      shapeType: MAPPINGS?.shape?.findIndex(item=>item.id === data?.shape?.id) + 1,
      productionMarkerType: typeof data?.shape === 'number' ? data?.shape : undefined,
      lineWidth: MAPPINGS?.sizes?.indexOf(data?.sizeShape) + 1,
      markerSizeType: MAPPINGS?.sizes?.indexOf(data?.sizeShape) + 1,
      hasBorder: data?.border
    },
    labelOption: {
      font: data?.font?.name,
      fontSizeType: parseInt(data?.sizeFont?.value) || undefined,
      positionType: MAPPINGS?.positions?.findIndex(item=>item.label === data?.position?.label) + 1
    }
  }
}

